import {
  Badge,
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
} from '@cardboard-ui/react';
import { t, Trans } from '@lingui/macro';
import { TextLink } from 'apps/TenantApp/components/TextLink';
import { MessageIcon, ProfileIcon } from 'components/icons';
import { UserAvatar } from 'components/UserAvatar';
import React from 'react';
import { generatePath } from 'react-router-dom';
import { graphql, useFragment } from 'utils/graphClient';
import { hasOwnerRole } from 'utils/membership';
import { INNER_CHAT_LOOKUP_DIRECT_PATH, MEMBER_PATH } from 'utils/routes';
import { UserInfoPopover_member$key } from './__generated__/UserInfoPopover_member.graphql';
import PrivateNote from 'apps/TenantApp/components/PrivateNote';

const fragment = graphql`
  fragment UserInfoPopover_member on Member {
    id
    name
    email
    roles
    ...UserAvatar_member
  }
`;

interface UserInfoPopoverProps {
  member: UserInfoPopover_member$key;
  children: React.ReactElement;
}

export const UserInfoPopover = ({
  member: _member,
  children,
}: UserInfoPopoverProps) => {
  const member = useFragment(fragment, _member);
  const { id, name, email, roles, ...avatarProps } = member;
  const isOrgOwner = hasOwnerRole(roles);

  const handleMessageClick = () => {
    const directChatPath = generatePath(INNER_CHAT_LOOKUP_DIRECT_PATH, {
      lookupId: id,
    });
    window.__openChatPath && window.__openChatPath(directChatPath);
  };

  const orgOwnerBadge = (
    <Badge colorScheme="green">
      <Trans comment="Short version of `Organisation owner`. This is used in a badge, and should be as short as possible.">
        Org. owner
      </Trans>
    </Badge>
  );
  const orgMemberBadge = (
    <Badge colorScheme="gray">
      <Trans comment="Short version of `Organisation member`. This is used in a badge, and should be as short as possible.">
        Org. member
      </Trans>
    </Badge>
  );

  return (
    <Popover placement="bottom-start" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>{children}</PopoverTrigger>
          <Portal>
            <Box
              sx={{
                '& .chakra-popover__popper': {
                  zIndex: 'popover',
                },
              }}
            >
              <PopoverContent>
                <PopoverBody p={4}>
                  <Stack spacing={4} w="100%">
                    <HStack>
                      <UserAvatar member={avatarProps} size="lg" />
                      <Stack spacing={0}>
                        <Heading size="sm">{name}</Heading>
                        <Box>{isOrgOwner ? orgOwnerBadge : orgMemberBadge}</Box>
                      </Stack>
                    </HStack>
                    <HStack>
                      <Button
                        as={Link}
                        size="sm"
                        variant="outline"
                        to={generatePath(MEMBER_PATH, { memberId: id })}
                        leftIcon={<ProfileIcon />}
                        w="full"
                      >{t`View user`}</Button>

                      <Button
                        size="sm"
                        variant="outline"
                        w="full"
                        leftIcon={<MessageIcon />}
                        onClick={() => {
                          onClose();
                          setTimeout(handleMessageClick, 0);
                        }}
                      >{t`Message`}</Button>
                    </HStack>
                    <Divider w="100%" />
                    <Stack spacing={0}>
                      <Heading size="xs">{t`Email`}</Heading>
                      <TextLink toMail={email}>{email}</TextLink>
                    </Stack>

                    <PrivateNote
                      nodeId={id}
                      headingProps={{ size: 'xs' }}
                      boxProps={{
                        bg: 'gray.50',
                        _dark: { bg: 'blackAlpha.100' },
                      }}
                    />
                  </Stack>
                </PopoverBody>
              </PopoverContent>
            </Box>
          </Portal>
        </>
      )}
    </Popover>
  );
};
